import { AMBIENT, mountAmbientBackground } from './ambient-background';
import { initLandingAudio, createMuteToggle } from './landing-audio';
import { getLocale, setLocale, t, type Locale, type Translations } from './i18n/i18n';
import { languageSwitcherHtml } from './i18n/language-switcher';
// Registers the ALTCHA widget's own Spanish strings ("No soy un robot", …)
// under the "es-es" language code; the widget picks them up via its
// `language` attribute (see localizeAltcha). Must load after 'altcha' itself,
// which every auth screen imports before this module runs.
import 'altcha';
import 'altcha/i18n/es-es';

/**
 * Persistent auth backdrop — video + landing audio survive login/register
 * route switches. The backdrop lives in a layer BEHIND `#app` so core
 * renderers can overwrite `app.innerHTML` without destroying it.
 *
 * Architecture:
 *   <body>
 *     <div id="auth-backdrop">           ← persistent, behind app
 *       <div data-ambient>  (video)
 *       <div ambient-scrim>
 *     </div>
 *     <div id="app">                     ← core renderers write here
 *       <form ...>
 *     </div>
 */

let backdropEl: HTMLElement | null = null;
let muteToggleRef: { el: HTMLElement; destroy: () => void } | null = null;
let audioTeardownRef: (() => void) | null = null;
let langSwitcherEl: HTMLElement | null = null;
let alive = false;

function ensureBackdrop(): void {
  if (alive && backdropEl && document.body.contains(backdropEl)) return;

  backdropEl = document.createElement('div');
  backdropEl.id = 'auth-backdrop';
  backdropEl.className = 'fixed inset-0 overflow-hidden pointer-events-none';
  backdropEl.style.zIndex = '0';
  document.body.insertBefore(backdropEl, document.body.firstChild);
  mountAmbientBackground(backdropEl, AMBIENT.auth);

  audioTeardownRef = initLandingAudio();
  muteToggleRef = createMuteToggle();
  document.body.appendChild(muteToggleRef.el);
  langSwitcherEl = createAuthLanguageSwitcher();
  document.body.appendChild(langSwitcherEl);

  alive = true;
}

/**
 * Enhances an auth screen: ensures the persistent video backdrop exists,
 * makes the core-rendered form transparent so the video shows through,
 * and applies register-form tweaks (password strength position, ToC margin).
 *
 * Returns a teardown that ONLY cleans up per-screen state (not the
 * backdrop — that persists for the next auth screen).
 */
export function enhanceAuthScreen(root: HTMLElement): () => void {
  ensureBackdrop();

  const container = root.querySelector('.min-h-screen') as HTMLElement | null;
  if (container) {
    container.classList.remove('bg-auth-gradient');
    container.style.background = 'transparent';
    container.style.position = 'relative';
    container.style.zIndex = '1';
  }

  tweakRegisterForm(root);
  addBackToLanding(root);
  localizeAltcha(root);
  const stopTranslating = translateCoreAuthText(root);

  return () => {
    // Per-screen cleanup only — backdrop stays
    stopTranslating();
  };
}

/**
 * Destroys the persistent auth backdrop. Call when leaving the auth flow
 * entirely (navigating to game, landing page, etc.).
 */
export function destroyAuthShell(): void {
  if (!alive) return;
  muteToggleRef?.destroy();
  muteToggleRef = null;
  audioTeardownRef?.();
  audioTeardownRef = null;
  backdropEl?.remove();
  backdropEl = null;
  langSwitcherEl?.remove();
  langSwitcherEl = null;
  alive = false;
}

/**
 * Post-render DOM tweaks for the register form (core file, can't edit):
 * - Move password strength bar under the repeat-password field
 * - Add extra margin after the ToC checkbox
 */
function tweakRegisterForm(container: HTMLElement | null): void {
  if (!container) return;

  const strengthWrapper = container.querySelector('#register-strength-bar')?.parentElement?.parentElement as HTMLElement | null;
  const repeatLabel = container.querySelector('#register-repeat-password')?.closest('label') as HTMLElement | null;
  if (strengthWrapper && repeatLabel && repeatLabel.parentElement) {
    repeatLabel.parentElement.insertBefore(strengthWrapper, repeatLabel.nextSibling);
  }

  const termsCheckbox = container.querySelector('#register-terms');
  const termsContainer = termsCheckbox?.closest('div.mb-3\\.5') as HTMLElement | null;
  if (termsContainer) {
    termsContainer.style.marginBottom = '1.5rem';
  }
}

/**
 * Every core auth card (login, register, forgot/reset password, verify
 * email — core files, can't edit) gets a "← Back" link above its heading,
 * back to the landing page. A plain same-origin `<a href="/">` — main.ts's
 * global link interceptor turns it into an SPA navigate('/'), which also
 * tears down the auth backdrop (`/` is not an auth path).
 *
 * Skipped on check-email: that screen is only shown to a signed-in,
 * unverified user, and main.ts routes such a user from `/` straight back
 * to `/check-email` — the link would loop. It has its own "Sign out".
 */
function addBackToLanding(container: HTMLElement): void {
  const card = container.querySelector('.bg-auth-card');
  if (!card || card.querySelector('[data-back-to-landing]') || card.querySelector('#check-email-logout')) return;
  const link = document.createElement('a');
  link.href = '/';
  link.dataset.backToLanding = '';
  link.className =
    '-mt-3 -ml-2 mb-2 inline-flex items-center gap-1 min-h-11 px-2 rounded text-sm text-text-dim hover:text-gold transition-colors duration-[120ms] touch-manipulation';
  link.innerHTML = `<span aria-hidden="true">←</span>`;
  link.append(t('common.back'));
  card.insertBefore(link, card.firstChild);
}

/**
 * Compact EN/ES switcher pinned to the top-right of every auth screen, so a
 * player who lands straight on /login or /register (an e-mail link, a
 * bookmark) can still pick a language — not only via the landing header.
 * Same behaviour as the landing/settings switchers: persist, then reload.
 */
function createAuthLanguageSwitcher(): HTMLElement {
  const el = document.createElement('div');
  el.id = 'auth-language-switcher';
  el.className = 'fixed top-[calc(env(safe-area-inset-top,0px)+1rem)] right-4 z-50 bg-surface-2/80 backdrop-blur rounded';
  el.innerHTML = languageSwitcherHtml('compact');
  el.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest('[data-action="set-locale"]') as HTMLElement | null;
    const next = btn?.dataset.locale as Locale | undefined;
    if (!next || next === getLocale()) return;
    setLocale(next);
    window.location.reload();
  });
  return el;
}

/** ALTCHA language codes for our locales (bundles registered by the imports above). English is the widget default. */
const ALTCHA_LANGUAGE: Partial<Record<Locale, string>> = { es: 'es-es' };

function localizeAltcha(root: HTMLElement): void {
  const language = ALTCHA_LANGUAGE[getLocale()];
  if (!language) return;
  root.querySelectorAll('altcha-widget').forEach((w) => w.setAttribute('language', language));
}

/**
 * The core auth screens (login, forgot/reset password, verify e-mail, check
 * e-mail) are template-owned and hardcode English — they can't call t(). So
 * they are translated after the fact: every text node / aria-label whose
 * exact (trimmed) English text is listed here is swapped for its translation,
 * and a MutationObserver repeats that for text the screens write later
 * ("Signing in…", validation errors, "Email verified…"). The game-owned
 * register screen (engine/register-screen.ts) uses t() directly and needs
 * none of this. Also covers a few fixed backend auth errors.
 */
const CORE_AUTH_TEXT: Record<string, keyof Translations> = {
  'Welcome back': 'auth.login.title',
  'Sign in to continue.': 'auth.login.subtitle',
  Email: 'auth.email',
  Password: 'auth.password',
  'Show password': 'auth.showPassword',
  'Sign in': 'auth.login.submit',
  'Signing in…': 'auth.login.submitting',
  'Forgot password?': 'auth.login.forgot',
  'Create account': 'auth.createAccount',
  'Please wait for verification to complete.': 'auth.waitVerification',
  'Reset password': 'auth.forgot.title',
  "Enter your email and we'll send a reset link.": 'auth.forgot.subtitle',
  'If that email exists, we sent a reset link. Check your inbox.': 'auth.forgot.success',
  'Send reset link': 'auth.forgot.submit',
  'Sending…': 'auth.forgot.submitting',
  'Back to sign in': 'auth.backToSignIn',
  'Choose a new password': 'auth.reset.title',
  "You'll be redirected to sign in after reset.": 'auth.reset.subtitle',
  'New password (min 8 chars)': 'auth.reset.newPassword',
  'Set new password': 'auth.reset.submit',
  'Saving…': 'auth.reset.submitting',
  'Verifying email…': 'auth.verify.title',
  'One moment.': 'auth.verify.pending',
  'Email verified. You can now sign in.': 'auth.verify.done',
  'Verify your email': 'auth.check.title',
  'We sent a verification link to': 'auth.check.sentTo',
  'Check your inbox and click the link to continue.': 'auth.check.instructions',
  'Sign out': 'common.signOut',
  'Invalid credentials': 'auth.error.invalidCredentials',
  'Account banned': 'auth.error.banned',
  'Account could not be created. If you already have an account, try logging in or resetting your password.': 'auth.error.registerConflict',
};

const VERIFY_FAILED_PREFIX = 'Verification failed: ';

function translateText(text: string): string | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const key = CORE_AUTH_TEXT[trimmed];
  let translated: string | null = null;
  if (key) translated = t(key);
  else if (trimmed.startsWith(VERIFY_FAILED_PREFIX)) {
    translated = t('auth.verify.failed', { reason: trimmed.slice(VERIFY_FAILED_PREFIX.length) });
  }
  if (translated === null || translated === trimmed) return null;
  // Keep the surrounding whitespace — it is what separates inline links.
  return text.replace(trimmed, translated);
}

function translateSubtree(node: Node): void {
  if (node.nodeType === Node.TEXT_NODE) {
    const next = translateText(node.nodeValue ?? '');
    if (next !== null) node.nodeValue = next;
    return;
  }
  if (!(node instanceof Element)) return;
  const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    const next = translateText(n.nodeValue ?? '');
    if (next !== null) n.nodeValue = next;
  }
  const withAria = [node, ...node.querySelectorAll('[aria-label]')];
  for (const el of withAria) {
    const label = el.getAttribute('aria-label');
    const next = label ? translateText(label) : null;
    if (next !== null) el.setAttribute('aria-label', next);
  }
}

export function translateCoreAuthText(root: HTMLElement): () => void {
  if (getLocale() === 'en') return () => {};
  translateSubtree(root);
  // Text nodes written later (button labels while submitting, error/status
  // messages). Writing a translation fires another mutation, but the new
  // text is no longer a key in CORE_AUTH_TEXT, so it settles immediately.
  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type === 'characterData') translateSubtree(m.target);
      else m.addedNodes.forEach(translateSubtree);
    }
  });
  observer.observe(root, { subtree: true, childList: true, characterData: true });
  return () => observer.disconnect();
}
